import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  List,
  ListItem,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../store";
import {
  approveAllocation,
  rejectAllocation,
} from "../../store/slices/analysisSlice";
import {
  getRegionDisplayName,
  RegionDeficitAllocation,
  ApprovalStatus,
  AllocationResult,
  BusinessComparisonMetric,
} from "../../types";
import { CheckpointSummaryCard } from "../index";
import { StrategySelectionView } from "../checkpoint/StrategySelectionView";
import { AgentThinkingDetails } from "../chat/AgentThinkingDetails";
import { apiService } from "../../services/api";
import { useScenarioStream } from "../../hooks/useScenarioStream";

// --- Types & Constants ---

const STATUS_CONFIG: Record<
  ApprovalStatus,
  { color: string; bg: string; border: string; label: string }
> = {
  proposed: {
    color: "#f57c00",
    bg: "#fff8e1",
    border: "#ffcc80",
    label: "⏳ AWAITING APPROVAL",
  },
  approved: {
    color: "#388e3c",
    bg: "#e8f5e9",
    border: "#a5d6a7",
    label: "✅ APPROVED",
  },
  rejected: {
    color: "#d32f2f",
    bg: "#ffebee",
    border: "#ef9a9a",
    label: "❌ REJECTED",
  },
};

interface StatItemProps {
  label: string;
  value: string | number;
  subtext?: string;
  subtextColor?: string;
}

const StatItem: React.FC<StatItemProps> = ({
  label,
  value,
  subtext,
  subtextColor,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <Typography
      sx={{
        fontSize: "10px",
        color: "#9e9e9e",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        marginBottom: "4px",
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "28px",
        fontWeight: 700,
        color: "#1a1a2e",
      }}
    >
      {typeof value === "number" ? value.toLocaleString() : value}
    </Typography>
    {subtext && (
      <Typography
        sx={{
          fontSize: "11px",
          color: subtextColor || "error.main",
          marginTop: "2px",
          fontWeight: 500,
        }}
      >
        {subtext}
      </Typography>
    )}
  </Box>
);

const formatBusinessComparisonValue = (metric: BusinessComparisonMetric, value: number): string => {
  switch (metric.format) {
    case "currency":
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    case "percent":
      return `${value.toFixed(1)}%`;
    case "days":
      return `${value.toFixed(1)} days`;
    case "number_1dp":
      return value.toFixed(1);
    default:
      return Math.round(value).toLocaleString();
  }
};

const formatPercentDelta = (metric: BusinessComparisonMetric): string | null => {
  const baseline = Number(metric.baseline_value || 0);
  if (Math.abs(baseline) < 1e-9) return null;
  const percent = (Number(metric.delta || 0) / Math.abs(baseline)) * 100;
  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
};

const formatDirectionalDelta = (metric: BusinessComparisonMetric, baselineLabel: string): string => {
  const delta = Number(metric.delta || 0);
  const absDelta = Math.abs(delta);
  const baselineName = baselineLabel.toLowerCase();
  const label = String(metric.label || "").toLowerCase();

  if (absDelta < 1e-9) {
    return `Flat vs ${baselineName}`;
  }

  if (metric.format === "percent") {
    return `${absDelta.toFixed(1)} pts ${delta > 0 ? "higher" : "lower"} vs ${baselineName}`;
  }

  if (label.includes("shortfall")) {
    return `${Math.round(absDelta).toLocaleString()} ${delta > 0 ? "more short" : "less short"} vs ${baselineName}`;
  }

  if (metric.format === "currency") {
    return `${formatBusinessComparisonValue(metric, absDelta)} ${delta > 0 ? "higher" : "lower"} vs ${baselineName}`;
  }

  if (metric.format === "days") {
    return `${absDelta.toFixed(1)} days ${delta > 0 ? "slower" : "faster"} vs ${baselineName}`;
  }

  return `${formatBusinessComparisonValue(metric, absDelta)} ${delta > 0 ? "higher" : "lower"} vs ${baselineName}`;
};

const formatPercentBaselineNote = (metric: BusinessComparisonMetric): string | null => {
  const percentDelta = formatPercentDelta(metric);
  if (!percentDelta) return null;
  return `${percentDelta} vs baseline`;
};

const formatShortDate = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const EMPHASIS_PATTERN =
  /\b(?:Gulf|Chicago|Los Angeles|Southeast Toyota|New York|Boston|Kansas City|Central Atlantic|Cincinnati|Portland|San Francisco|Denver|Northeast|Midwest|Central regions|coastal regions)\b|\$[\d,]+(?:\.\d+)?|\b\d+(?:\.\d+)?%|\b\d+(?:\.\d+)?\s+days?\b/g;

const renderHighlightedText = (text: string, accentColor = "#b45309") => {
  const matches = Array.from(text.matchAll(EMPHASIS_PATTERN));
  if (!matches.length) return text;

  const fragments: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    const value = match[0];
    const start = match.index ?? 0;
    if (start > cursor) {
      fragments.push(text.slice(cursor, start));
    }
    fragments.push(
      <Box
        component="span"
        key={`${value}-${start}-${index}`}
        sx={{ fontWeight: 700, color: accentColor }}
      >
        {value}
      </Box>,
    );
    cursor = start + value.length;
  });

  if (cursor < text.length) {
    fragments.push(text.slice(cursor));
  }
  return fragments;
};

const BEST_AVAILABLE_LABELS: Record<string, string> = {
  exact_actualized: "Observed outcome from a directly comparable approved case",
  close_actualized: "Observed outcome from a similar approved case",
  approved_anchor: "Approved recommendation baseline",
  none: "No supporting evidence",
};

const ComparisonMetricSection: React.FC<{
  title: string;
  subtitle?: string;
  summary?: string;
  metrics: BusinessComparisonMetric[];
  leftLabel: string;
  rightLabel: string;
}> = ({ title, subtitle, summary, metrics, leftLabel, rightLabel }) => {
  if (!metrics.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        bgcolor: "#fff",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1f2937" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "12px", color: "#64748b", mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
        {summary && (
          <Typography sx={{ fontSize: "12px", color: "#334155", mt: 0.75, lineHeight: 1.55 }}>
            {renderHighlightedText(summary)}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
        {metrics.map((metric) => {
          const trendColor =
            metric.trend === "improved" ? "#166534" : metric.trend === "regressed" ? "#b91c1c" : "#475569";
          const delta = Number(metric.delta || 0);
          return (
            <Box
              key={metric.key}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 700, mb: 1 }}>
                {metric.label}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: "11px", color: "#475569" }}>
                  {leftLabel}:{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {formatBusinessComparisonValue(metric, Number(metric.current_value || 0))}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#475569" }}>
                  {rightLabel}:{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {formatBusinessComparisonValue(metric, Number(metric.baseline_value || 0))}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: "11px", color: trendColor, fontWeight: 700, mt: 0.5 }}>
                  {formatDirectionalDelta(metric, rightLabel)}
                </Typography>
                {formatPercentBaselineNote(metric) && (
                  <Typography sx={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                    {formatPercentBaselineNote(metric)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// --- Sub-Components ---

const SurplusView: React.FC<{ plan: any; titlePrefix?: string }> = ({ plan, titlePrefix }) => {
  if (!plan) return null;

  return (
    <Box sx={{ marginBottom: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          marginBottom: "6px",
        }}
      >
        <Typography sx={{ fontSize: 18 }}>📈</Typography>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a2e" }}
        >
          {titlePrefix ? `${titlePrefix} ` : ""}Surplus Allocation
        </Typography>
        {plan.deviation_batch_id && (
          <Chip label={plan.deviation_batch_id} size="small" sx={{ fontSize: '10px', height: 20 }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.secondary",
          marginBottom: "14px",
        }}
      >
        Units distributed to regions with highest velocity, capacity, and cost efficiency
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 2,
          marginBottom: "16px",
          paddingBottom: "16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <StatItem
          label="Units Allocated"
          value={plan.total_units_allocated || 0}
        />
        <StatItem
          label="Transport Cost"
          value={`$${((plan.total_transport_cost || 0) / 1000).toFixed(0)}K`}
        />
        <StatItem
          label="Holding Cost"
          value={`$${((plan.total_holding_cost || 0) / 1000).toFixed(0)}K`}
        />
        <StatItem
          label="Avg Days to Sell"
          value={`${(plan.weighted_avg_days_to_sell || 0).toFixed(0)} days`}
        />
        <StatItem
          label="Regions Selected"
          value={plan.allocations?.length || 0}
        />
      </Box>

      <TableContainer sx={{ marginBottom: "14px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Region</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">Allocation %</TableCell>
              <TableCell align="right">Total Cost</TableCell>
              <TableCell align="right">Days to Sell</TableCell>
              <TableCell align="right">Velocity Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...(plan.allocations || [])]
              .sort((a: any, b: any) => (b.allocated_units || 0) - (a.allocated_units || 0))
              .map((allocation: any) => (
                <TableRow key={allocation.region}>
                  <TableCell sx={{ fontWeight: 500, color: "#1a1a2e" }}>
                    {getRegionDisplayName(allocation.region)}
                  </TableCell>
                  <TableCell align="right">
                    {allocation.allocated_units?.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {((allocation.allocation_percent || 0) * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell align="right">
                    ${((allocation.total_cost || 0) / 1000).toFixed(0)}K
                  </TableCell>
                  <TableCell align="right">
                    {(allocation.projected_days_to_sell || 0).toFixed(0)}
                  </TableCell>
                  <TableCell align="right">
                    {(allocation.velocity_score || 0).toFixed(0)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {plan.excluded_regions && plan.excluded_regions.length > 0 && (
        <Accordion defaultExpanded={false} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningIcon sx={{ fontSize: 18, color: "#f57c00" }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                Excluded Regions ({plan.excluded_regions.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {plan.excluded_regions.map((excluded: any) => (
                <ListItem key={excluded.region} sx={{ paddingLeft: 0 }}>
                  <Typography sx={{ fontSize: "12px", color: "#4a4a4a" }}>
                    <strong>{getRegionDisplayName(excluded.region)}</strong>: {excluded.primary_reason || excluded.exclusion_summary}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

const DeficitView: React.FC<{ plan: any; titlePrefix?: string; regionContexts?: any[] }> = ({ plan, titlePrefix, regionContexts }) => {
  if (!plan) return null;

  const deficitData = {
    allocations: plan.allocations || [],
    totalSupply: plan.total_supply || 0,
    totalDemand: plan.total_demand || 0,
    fulfillmentRate: plan.fulfillment_rate || 0,
    shortfall: plan.shortfall || 0,
  };

  // Helper to find priority
  const getPriority = (region: string) => {
    if (!regionContexts) return null;
    const ctx = regionContexts.find(c => c.region === region);
    return ctx?.strategic_priority;
  };

  return (
    <Box sx={{ marginBottom: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          marginBottom: "6px",
        }}
      >
        <Typography sx={{ fontSize: 18 }}>📉</Typography>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a2e" }}
        >
          {titlePrefix ? `${titlePrefix} ` : ""}Shortage Allocation
        </Typography>
        {plan.deviation_batch_id && (
          <Chip label={plan.deviation_batch_id} size="small" sx={{ fontSize: '10px', height: 20 }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.secondary",
          marginBottom: "14px",
        }}
      >
        Units distributed fairly: everyone gets a minimum, then split by
        demand, adjusted for sales performance.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2,
          marginBottom: "16px",
          paddingBottom: "16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <StatItem
          label="Supply Available"
          value={deficitData.totalSupply}
        />
        <StatItem label="Total Regional Demand" value={deficitData.totalDemand} />
        <StatItem
          label="Fulfillment Rate"
          value={`${Math.round(deficitData.fulfillmentRate * 100)}%`}
        />
        <StatItem
          label="Shortfall"
          value={deficitData.shortfall}
          subtext={`▼ ${deficitData.shortfall} units short`}
        />
      </Box>

      <TableContainer sx={{ marginBottom: "14px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="right">Regional Demand</TableCell>
              <TableCell align="right">Allocated</TableCell>
              <TableCell align="right">Shortfall</TableCell>
              <TableCell>Fulfilled</TableCell>
              <TableCell align="right">Merit Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...deficitData.allocations]
              .sort((a: any, b: any) => (a.rank || 99) - (b.rank || 99))
              .map((allocation: RegionDeficitAllocation) => {
                const priority = getPriority(allocation.region);
                return (
                  <TableRow key={allocation.region}>
                    <TableCell
                      sx={{ color: "primary.main", fontWeight: 600 }}
                    >
                      #{allocation.rank}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, color: "#1a1a2e" }}>
                      {getRegionDisplayName(allocation.region)}
                    </TableCell>
                    <TableCell>
                      {priority && (
                        <Chip
                          label={priority}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            fontWeight: 700,
                            bgcolor: priority === 'Urgent' ? '#ffebee' : priority === 'High' ? '#e3f2fd' : '#f5f5f5',
                            color: priority === 'Urgent' ? '#d32f2f' : priority === 'High' ? '#1976d2' : '#757575'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{allocation.demand}</TableCell>
                    <TableCell align="right">
                      {allocation.allocated}
                    </TableCell>
                    <TableCell align="right">
                      {allocation.shortfall}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={(allocation.fulfillment_rate || 0) * 100}
                          sx={{
                            width: 50,
                            height: 6,
                            borderRadius: 3,
                          }}
                        />
                        <Typography sx={{ fontSize: "13px" }}>
                          {Math.round(
                            (allocation.fulfillment_rate || 0) * 100,
                          )}
                          %
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {(allocation.merit_score || 0).toFixed(1)}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Accordion defaultExpanded={false} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BarChartIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              How Units Were Distributed
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginBottom: "12px",
            }}
          >
            Each region's allocation was calculated in 3 steps:
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "12px",
            }}
          >
            {[...deficitData.allocations]
              .sort((a, b) => (a.rank || 99) - (b.rank || 99))
              .map((allocation: RegionDeficitAllocation) => {
                const fulfillmentPercent = Math.round(
                  (allocation.fulfillment_rate || 0) * 100,
                );
                const meritColor =
                  allocation.merit_adjustment >= 0 ? "#2e7d32" : "#d32f2f";

                return (
                  <Box
                    key={allocation.region}
                    sx={{
                      backgroundColor: '#ffffff',
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#1a1a2e",
                        }}
                      >
                        {getRegionDisplayName(allocation.region)} →{" "}
                        {allocation.allocated} units
                      </Typography>
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      >
                        ({fulfillmentPercent}% of {allocation.demand})
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "12px", color: "text.secondary" }}
                        >
                          Guaranteed minimum:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          {allocation.base_units} units
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "12px", color: "text.secondary" }}
                        >
                          Fair share:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          {allocation.proportional_units} units
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "12px", color: "text.secondary" }}
                        >
                          {allocation.merit_adjustment >= 0
                            ? "Performance bonus:"
                            : "Performance adj:"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: meritColor,
                          }}
                        >
                          {allocation.merit_adjustment >= 0 ? "+" : ""}
                          {allocation.merit_adjustment} (score:{" "}
                          {(allocation.merit_score || 0).toFixed(0)})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </AccordionDetails>
      </Accordion>


    </Box>
  );
};

const ReconciliationView: React.FC<{ result: any }> = ({ result }) => {
  if (!result.reconciliation_result) return null;

  // Helper to get total allocation for share calculation
  const totalAllocated = Object.values(result.reconciliation_result.regional_allocations || {}).reduce((a: any, b: any) => a + b, 0) as number;

  return (
    <Box sx={{ marginBottom: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          marginBottom: "6px",
        }}
      >
        <Typography sx={{ fontSize: 18 }}>⚖️</Typography>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a2e" }}
        >
          Reconciliation Allocation
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.secondary",
          marginBottom: "14px",
        }}
      >
        Optimized distribution matching regional demand patterns
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
        {/* National Mix Breakdown */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Production Mix (National)</Typography>
          <TableContainer sx={{ marginBottom: "16px", border: '1px solid #eee', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle Spec</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Fulfillment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.reconciliation_result.allocations || {})
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([config, units]) => {
                    const demand = result.reconciliation_result.demand?.[config] || 0;
                    const fulfillment = demand > 0 ? (units as number) / demand : 0;

                    return (
                      <TableRow key={config}>
                        <TableCell sx={{ fontWeight: 500, color: "#455a64", fontSize: '11px' }}>
                          {config}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          {demand.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {(units as number).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {(fulfillment * 100).toFixed(0)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Regional Breakdown */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Regional Consignment</Typography>
          <TableContainer sx={{ marginBottom: "16px", border: '1px solid #eee', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Allocated Units</TableCell>
                  <TableCell align="right">% Share</TableCell>
                  <TableCell align="right">Fulfillment %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.reconciliation_result.regional_allocations || {})
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([region, units]) => {
                    const share = totalAllocated > 0 ? (units as number) / totalAllocated : 0;
                    // Try to get baseline demand from scenario data if available
                    const baselineDemand = result.reconciliation_scenario?.regions?.[region] || 0;
                    const fulfillment = baselineDemand > 0 ? (units as number) / baselineDemand : 0;

                    return (
                      <TableRow key={region}>
                        <TableCell sx={{ fontWeight: 500, color: "#1a1a2e" }}>
                          {getRegionDisplayName(region)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${(units as number).toLocaleString()} units`}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: "12px",
                              fontWeight: 600,
                              bgcolor: "#e3f2fd",
                              color: "#1565c0",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {(share * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {baselineDemand > 0 ? `${(fulfillment * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};


const VolumeSummary: React.FC<{ result: any }> = ({ result }) => {
  if (result.plan_type !== "reconciliation") return null;

  let demand = 0;
  let supply = 0;
  let allocated = 0;
  let show = false;

  if (result.reconciliation_result) {
    const res = result.reconciliation_result;
    demand = Object.values(res.demand || {}).reduce((a: number, b: any) => a + (b as number), 0);
    supply = result.user_modifications?.national_build_target || result.reconciliation_scenario?.baseline?.national || 0;
    allocated = Object.values(res.regional_allocations || {}).reduce((a: number, b: any) => a + (b as number), 0);
    show = true;
  } else if (result.deficit_allocation_plan) {
    const res = result.deficit_allocation_plan;
    demand = res.total_demand || 0;
    supply = res.total_supply || 0;
    allocated = res.total_allocated || 0;
    show = true;
  } else if (result.mixed_allocation_plan) {
    const res = result.mixed_allocation_plan;
    demand = (res.deficit_plans || []).reduce((s: number, p: any) => s + (p.total_demand || 0), 0);
    supply = (res.deficit_plans || []).reduce((s: number, p: any) => s + (p.total_supply || 0), 0);
    allocated = (res.deficit_plans || []).reduce((s: number, p: any) => s + (p.total_allocated || 0), 0) +
      (res.surplus_plans || []).reduce((s: number, p: any) => s + (p.total_units_allocated || 0), 0);

    const surplusUnits = (res.surplus_plans || []).reduce((s: number, p: any) => s + (p.total_units_allocated || 0), 0);
    demand += surplusUnits;
    supply += surplusUnits;
    show = true;
  }

  if (!show) return null;

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f7f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 1, display: 'block' }}>
        Program Volume Summary
      </Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Total Demand</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{demand.toLocaleString()}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Supply Target</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{supply.toLocaleString()}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Allocated</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: allocated < supply ? 'warning.main' : 'success.main' }}>
            {allocated.toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, alignSelf: 'center', textAlign: 'right' }}>
          <Chip
            label={`${Math.round((allocated / (demand || 1)) * 100)}% Fulfillment`}
            size="small"
            color={allocated >= supply ? "success" : "warning"}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      </Box>
    </Box>
  );
};


// --- Main Component ---

const ResultsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const { resumeAnalysis } = useScenarioStream();
  const { result, isRunning, isTransitioning, isResumeFlow, pendingCheckpoint, traces, currentNode } = useAppSelector((state) => state.analysis);
  const [selectedComparisonCandidateId, setSelectedComparisonCandidateId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSelectedComparisonCandidateId(result?.business_comparison?.selected_candidate_id || null);
  }, [result?.run_id, result?.business_comparison?.selected_candidate_id]);

  const isStrategistGate = pendingCheckpoint?.strategy_options && pendingCheckpoint.strategy_options.length > 0;
  const isTacticalGate = pendingCheckpoint?.region_contexts && pendingCheckpoint.region_contexts.length > 0;

  // Show thinking details if it's running OR if we are at the first gate (strategist)
  // Resume handler for strategy selection (Gate 1)
  const handleStrategySelection = async (strategyId: string) => {
    if (!pendingCheckpoint || !pendingCheckpoint.scenario_id) return;

    // Use the hook to ensure SSE resumes correctly
    resumeAnalysis(pendingCheckpoint.scenario_id, {
      confirmation_id: pendingCheckpoint.confirmation_id,
      selected_strategy_id: strategyId
    });
  };

  if (isStrategistGate) {
    return (
      <Card sx={{
        height: "100%",
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 24px -4px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <CardContent sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: { xs: 2, md: 4 },
          '&:last-child': { pb: 4 }
        }}>
          {/* Integrated Agent Trace Section - Only show Strategist events at Gate 1 */}
          <Box sx={{ mb: 6 }}>
            <AgentThinkingDetails
              traces={traces}
              isComplete={false}
              includeNodes={['strategist']}
            />
          </Box>

          <StrategySelectionView
            options={pendingCheckpoint!.strategy_options!}
            onConfirm={handleStrategySelection}
          />
        </CardContent>
      </Card>
    );
  }

  // Handle tactical gate (Gate 2) - original checkpoint UI
  if (isTacticalGate) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <CheckpointSummaryCard modifications={result?.user_modifications} />
          {/* The actual Gate 2 UI components (sliders, table) go here or are handled by existing logic */}
          {/* For now, ensuring we don't break the existing Gate 2 if it relies on this panel */}
        </CardContent>
      </Card>
    )
  }

  if (isTransitioning) {
    return (
      <Card sx={{
        height: "100%",
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 24px -4px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.05)',
        bgcolor: '#fff'
      }}>
        <CardContent sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 6
        }}>
          <Box sx={{ position: 'relative', mb: 4 }}>
            <CircularProgress
              size={64}
              thickness={2}
              sx={{ color: '#f59e0b', opacity: 0.2 }}
            />
            <CircularProgress
              size={64}
              thickness={4}
              sx={{
                color: '#f59e0b',
                position: 'absolute',
                left: 0,
                animationDuration: '1.5s'
              }}
            />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#f59e0b'
            }}>
              <CheckIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, textAlign: 'center' }}>
            Injecting Strategic Directive...
          </Typography>
          <Typography sx={{ color: '#64748b', mb: 4, textAlign: 'center', maxWidth: '400px', fontWeight: 500 }}>
            The agents are now tuning the unit-level optimization parameters to align with your selected strategy.
          </Typography>

          {!isResumeFlow && (
            <Box sx={{ width: '100%', maxWidth: '600px' }}>
              <AgentThinkingDetails
                traces={traces}
                isComplete={false}
                excludeNodes={['orchestrator']}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!result && isRunning) {
    return (
      <Card sx={{
        height: "100%",
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 4px 24px -4px rgba(0,0,0,0.05)',
        bgcolor: '#fff',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: 4 }}>
          {isResumeFlow ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
              <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                Applying tactical directives...
              </Typography>
              <Typography sx={{ fontSize: '12px' }}>
                Agents are finalizing optimization with the approved gate inputs.
              </Typography>
              <CircularProgress size={24} sx={{ marginTop: 2 }} />
            </Box>
          ) : (
            <AgentThinkingDetails traces={traces} isComplete={false} />
          )}
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return null;
  }

  if (result.status === "failed") {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Alert severity="error">
            <AlertTitle>Analysis Failed</AlertTitle>
            {result.error || "An unknown error occurred"}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const approvalStatus = result.approval_status || "proposed";
  const statusConfig = STATUS_CONFIG[approvalStatus] || STATUS_CONFIG["proposed"];

  const handleApprove = () => {
    if (result.run_id) {
      dispatch(approveAllocation(result.run_id));
    }
  };

  const handleReject = () => {
    if (result.run_id) {
      dispatch(rejectAllocation(result.run_id));
    }
  };

  const comparisonSection = (() => {
    const payload = result.business_comparison || null;
    if (!payload) return null;

    const bestAvailableSource = payload.best_available?.source || "none";
    const comparisonCandidates = payload.comparison_candidates || [];
    const selectedCandidate =
      comparisonCandidates.find((candidate: any) => candidate.candidate_id === selectedComparisonCandidateId) ||
      comparisonCandidates[0] ||
      null;
    const activeSource = selectedCandidate?.source || bestAvailableSource;
    const metrics = selectedCandidate?.current_vs_selected_metrics || payload.metrics || [];
    const historicalMetrics = selectedCandidate?.approved_vs_actualized_metrics || [];
    const headerLabel = payload.anchor_mode === "whatif_in_session_baseline" ? "What-If History Context" : "Historical Context";
    const comparisonBrief = selectedCandidate?.brief || payload.comparison_brief || {};
    const primaryKicker =
      comparisonBrief.kicker ||
      BEST_AVAILABLE_LABELS[activeSource] ||
      "Historical precedent";
    const primarySummary =
      comparisonBrief.summary ||
      payload.actualized_evidence?.outcome_summary ||
      payload.headline?.summary ||
      "No approved or actualized evidence is currently strong enough to support a grounded comparison for this recommendation.";
    const primaryImplication =
      comparisonBrief.implication ||
      (payload.claim_suppressed
        ? "Confidence is not high enough for a strong directional claim."
        : undefined);
    const primaryHighlights = Array.isArray(comparisonBrief.highlights)
      ? comparisonBrief.highlights.filter((item): item is string => Boolean(item))
      : [];
    const historicalCase = selectedCandidate?.historical_case || {};
    const selectedModeLabel =
      selectedCandidate?.selected_reference_mode === "actualized_outcome"
        ? "actualized outcome"
        : "approved recommendation";
    const eventDate = formatShortDate(historicalCase?.event_date);
    const historicalCaseLabel =
      comparisonBrief.case_summary ||
      [
        historicalCase?.title,
        historicalCase?.subtitle ? `(${historicalCase.subtitle})` : null,
        eventDate ? `from ${eventDate}` : null,
      ]
        .filter((item): item is string => Boolean(item))
        .join(" ");
    const historicalCaseSummary =
      comparisonBrief.why_this_match ||
      historicalCase?.summary ||
      "Historical case summary unavailable.";
    const fallbackNote =
      activeSource === "approved_anchor"
        ? "This comparison is using recommendation history, not observed outcome proof."
        : null;

    return (
      <Box
        sx={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "16px",
          marginTop: "20px",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
          {headerLabel}
        </Typography>

        {comparisonCandidates.length > 1 && (
          <FormControl size="small" sx={{ width: { xs: "100%", sm: "360px" } }}>
            <Select
              value={selectedCandidate?.candidate_id || ""}
              onChange={(event) => setSelectedComparisonCandidateId(String(event.target.value))}
              displayEmpty
            >
              {comparisonCandidates.map((candidate: any) => (
                <MenuItem key={candidate.candidate_id} value={candidate.candidate_id}>
                  {candidate.rank ? `${candidate.rank}. ` : ""}
                  {candidate.label || candidate.selected_reference_label || candidate.candidate_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #eef2ff 0%, #ffffff 60%)",
              border: "1px solid #c7d2fe",
              borderRadius: "10px",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em", color: "#3730a3", textTransform: "uppercase" }}>
              Historical case
            </Typography>
            <Typography sx={{ fontSize: "15px", lineHeight: 1.45, color: "#1f2937", fontWeight: 700 }}>
              {historicalCaseLabel || "Historical case"}
            </Typography>
            <Typography sx={{ fontSize: "13px", lineHeight: 1.65, color: "#475569", fontWeight: 500 }}>
              {renderHighlightedText(historicalCaseSummary, "#3730a3")}
            </Typography>
          </Box>

          <Box
            sx={{
              background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 55%)",
              border: "1px solid #fed7aa",
              borderRadius: "10px",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em", color: "#9a3412", textTransform: "uppercase" }}>
              {primaryKicker}
            </Typography>
            <Typography sx={{ fontSize: "15px", lineHeight: 1.5, color: "#1f2937", fontWeight: 600 }}>
              {renderHighlightedText(primarySummary)}
            </Typography>
            {primaryImplication && (
              <Typography sx={{ fontSize: "12px", color: "#475569" }}>
                {renderHighlightedText(primaryImplication, "#0f766e")}
              </Typography>
            )}
            {fallbackNote && (
              <Typography sx={{ fontSize: "12px", color: "#475569" }}>
                {fallbackNote}
              </Typography>
            )}
            {primaryHighlights.length > 0 && (
              <List sx={{ padding: 0, margin: 0 }}>
                {primaryHighlights.map((item) => (
                  <ListItem
                    key={item}
                    sx={{
                      display: "list-item",
                      padding: 0,
                      marginLeft: "18px",
                      color: "#475569",
                    }}
                    >
                    <Typography sx={{ fontSize: "12px", color: "#475569" }}>
                      {renderHighlightedText(item)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <ComparisonMetricSection
            title="Today vs history"
            subtitle="Current recommendation vs the historical result from this similar case."
            summary={
              selectedCandidate?.brief?.current_vs_selected_summary ||
              payload.comparison_brief?.current_vs_selected_summary
            }
            metrics={metrics}
            leftLabel="Today"
            rightLabel="History"
          />
          <ComparisonMetricSection
            title="History: plan vs outcome"
            subtitle="What the historical recommendation planned versus what the business outcome actually delivered."
            summary={
              selectedCandidate?.brief?.approved_vs_actualized_summary ||
              payload.comparison_brief?.approved_vs_actualized_summary
            }
            metrics={historicalMetrics}
            leftLabel="Outcome"
            rightLabel="Planned"
          />
        </Box>

        {payload.filter_context?.requested_filters && (
          <Typography sx={{ fontSize: "11px", color: "#475569" }}>
            Requested filters: {Object.entries(payload.filter_context.requested_filters)
              .map(([k, v]) => `${k}=${(v || []).join(",")}`)
              .join(" | ")}
          </Typography>
        )}
      </Box>
    );
  })();

  return (
    <Card sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssessmentIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography
            sx={{ fontSize: "16px", fontWeight: 600, color: "#1a1a2e" }}
          >
            Results
          </Typography>
        </Box>
        <Chip
          label={statusConfig.label}
          size="small"
          sx={{
            backgroundColor: statusConfig.bg,
            color: statusConfig.color,
            border: `1px solid ${statusConfig.border}`,
            fontSize: "10px",
            fontWeight: 600,
            height: "24px",
          }}
        />
      </Box>

      <CardContent>
        <VolumeSummary result={result} />
        <CheckpointSummaryCard modifications={result.user_modifications} />

        {result.normalization_note?.shown && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              "& .MuiAlert-message": { fontSize: "12px", fontWeight: 500 },
            }}
          >
            {result.normalization_note.message}
          </Alert>
        )}

        {/* Case 1: Reconciliation (Full Program Reset) */}
        {result.reconciliation_result && (
          <ReconciliationView result={result} />
        )}

        {/* Case 2: Multi-Batch Allocation (Surplus) */}
        {result.multi_allocation_plan && result.multi_allocation_plan.batch_allocations && (
          result.multi_allocation_plan.batch_allocations.map((batch: any, idx: number) => (
            <SurplusView key={idx} plan={batch} titlePrefix={`Batch ${idx + 1}`} />
          ))
        )}

        {/* Case 3: Mixed Allocation (Surplus + Deficit) */}
        {result.mixed_allocation_plan && (
          <>
            {result.mixed_allocation_plan.surplus_plans?.map((plan: any, idx: number) => (
              <SurplusView key={`surplus-${idx}`} plan={plan} titlePrefix="(Surplus Batch)" />
            ))}
            {result.mixed_allocation_plan.deficit_plans?.map((plan: any, idx: number) => (
              <DeficitView key={`deficit-${idx}`} plan={plan} titlePrefix="(Shortage Batch)" regionContexts={result.region_contexts} />
            ))}
          </>
        )}

        {/* Case 4: Single Surplus Allocation */}
        {result.allocation_plan && !result.reconciliation_result && !result.multi_allocation_plan && !result.mixed_allocation_plan && (
          <SurplusView plan={result.allocation_plan} />
        )}

        {/* Case 5: Single Deficit Allocation */}
        {result.deficit_allocation_plan && !result.mixed_allocation_plan && (
          <DeficitView plan={result.deficit_allocation_plan} regionContexts={result.region_contexts} />
        )}


        {result.detailed_reasoning && (
          <Accordion defaultExpanded={false} variant="outlined" sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                  Detailed Reasoning
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {Array.isArray(result.detailed_reasoning) ? (
                <List dense>
                  {result.detailed_reasoning.map((item: string, index: number) => (
                    <ListItem key={index} sx={{ paddingLeft: 0 }}>
                      <Typography sx={{ fontSize: "12px", color: "#4a4a4a" }}>
                        • {item}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  sx={{ fontSize: "12px", color: "#4a4a4a", lineHeight: 1.6 }}
                >
                  {result.detailed_reasoning}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {result.risk_factors && result.risk_factors.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningIcon sx={{ fontSize: 18 }} />}
            sx={{
              marginTop: "14px",
              backgroundColor: "#fff8e1",
              borderLeft: "4px solid #ffc107",
              "& .MuiAlert-icon": {
                color: "#e65100",
              },
            }}
          >
            <AlertTitle
              sx={{ fontSize: "13px", fontWeight: 600, color: "#e65100" }}
            >
              Risk Factors
            </AlertTitle>
            <List dense sx={{ paddingLeft: 0 }}>
              {result.risk_factors.map((risk, index) => (
                <ListItem
                  key={index}
                  sx={{
                    paddingLeft: 0,
                    paddingTop: "2px",
                    paddingBottom: "2px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "12px", color: "#5d4037", lineHeight: 1.5 }}
                  >
                    • {risk}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {approvalStatus === "proposed" && result.run_id && (
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "14px",
              marginTop: "14px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={handleReject}
              sx={{
                padding: "10px 24px",
                borderColor: "#e74c3c",
                color: "#e74c3c",
                "&:hover": {
                  backgroundColor: "#fdf2f2",
                  borderColor: "#e74c3c",
                },
              }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleApprove}
              sx={{
                padding: "10px 24px",
                background: "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(46,204,113,0.35)",
                },
              }}
            >
              Approve
            </Button>
          </Box>
        )}

        {comparisonSection}
      </CardContent>
    </Card>
  );
};

export default ResultsPanel;
